import { useState, useEffect, useCallback } from "react";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail } from "firebase/auth";
import { collection, doc, setDoc, getDoc, getDocs, query, where, addDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import {
  POSITIONS, INNINGS_OPTS, inputStyle, POSITION_TIPS, BADGE_DEFINITIONS, SITUATION_TYPES,
} from "./constants";
import {
  genInviteCode, getAccuracyColor, calcLevel, calcStars, checkBadges,
} from "./utils";
import CoachDashboard from "./components/CoachDashboard";
import PositionSelect from "./components/PositionSelect";
import PlayScreen from "./components/PlayScreen";
import TeamPlays from "./components/TeamPlays";

// ── Landing ────────────────────────────────────────────────────────────────

function Landing({ onCoach, onPlayer, onGuest }) {
  return (
    <div style={{padding:"44px 24px",textAlign:"center"}}>
      <div style={{fontSize:52,marginBottom:12}}>⚾</div>
      <div style={{fontSize:22,fontWeight:700,color:"#231F20",fontFamily:"monospace",letterSpacing:"0.05em",textTransform:"uppercase",marginBottom:6}}>Clutch Position</div>
      <div style={{fontSize:13,color:"#999",marginBottom:40}}>Defensive Situations Trainer</div>
      <div style={{display:"flex",flexDirection:"column",gap:12,maxWidth:280,margin:"0 auto"}}>
        <button onClick={onCoach} style={{padding:"16px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:10,cursor:"pointer",fontSize:15,fontWeight:700,fontFamily:"monospace"}}>🏆 I'm a Coach</button>
        <button onClick={onPlayer} style={{padding:"16px",background:"white",color:"#231F20",border:"2.5px solid #231F20",borderRadius:10,cursor:"pointer",fontSize:15,fontWeight:700,fontFamily:"monospace"}}>⚾ I'm a Player</button>
        <div style={{display:"flex",alignItems:"center",gap:10,margin:"4px 0"}}>
          <div style={{flex:1,height:1,background:"#e0e0e0"}}/>
          <span style={{fontSize:11,color:"#bbb"}}>or</span>
          <div style={{flex:1,height:1,background:"#e0e0e0"}}/>
        </div>
        <button onClick={onGuest} style={{padding:"14px",background:"#f6f6f6",color:"#555",border:"1px solid #ddd",borderRadius:10,cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"monospace"}}>⚡ Quick Play — No Account</button>
        <div style={{fontSize:11,color:"#bbb",marginTop:-4}}>Jump in instantly · no data saved</div>
      </div>
    </div>
  );
}

// ── Guest Setup ────────────────────────────────────────────────────────────

function GuestSetup({ onDone, onBack }) {
  const [innings,setInnings]=useState(null), [name,setName]=useState("");
  const sel=INNINGS_OPTS.find(o=>o.innings===innings), ok=innings!==null;
  return (
    <div style={{padding:"28px 22px"}}>
      <button onClick={onBack} style={{fontSize:12,color:"#999",background:"none",border:"none",cursor:"pointer",marginBottom:16,padding:0}}>← Back</button>
      <div style={{textAlign:"center",marginBottom:26}}>
        <div style={{fontSize:36,marginBottom:8}}>⚡</div>
        <div style={{fontSize:18,fontWeight:700,color:"#231F20"}}>Quick Play</div>
        <div style={{fontSize:12,color:"#999",marginTop:4}}>No account needed · nothing saved</div>
      </div>
      <div style={{marginBottom:18}}>
        <label style={{fontSize:13,fontWeight:600,color:"#444",display:"block",marginBottom:7}}>First name (optional)</label>
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="Enter your name or leave blank" maxLength={20} style={{...inputStyle,marginBottom:0}}/>
      </div>
      <div style={{marginBottom:26}}>
        <label style={{fontSize:13,fontWeight:600,color:"#444",display:"block",marginBottom:10,textAlign:"center"}}>How many innings do you play?</label>
        <div style={{display:"flex",justifyContent:"center",gap:16,maxWidth:280,margin:"0 auto"}}>
          {INNINGS_OPTS.map(o=>{
            const active=innings===o.innings;
            return <button key={o.innings} onClick={()=>setInnings(o.innings)} style={{flex:1,padding:"18px 6px",borderRadius:9,cursor:"pointer",textAlign:"center",border:active?"2.5px solid #231F20":"1.5px solid #e0e0e0",background:active?"#231F20":"white",color:active?"#1FD71D":"#333",transition:"all 0.15s"}}>
              <div style={{fontSize:30,fontWeight:700,fontFamily:"monospace"}}>{o.innings}</div>
              <div style={{fontSize:11,marginTop:3,opacity:0.85}}>{o.tag}</div>
            </button>;
          })}
        </div>
        {sel&&<div style={{textAlign:"center",fontSize:12,color:"#231F20",fontWeight:700,marginTop:9}}>Level: {sel.level.charAt(0).toUpperCase()+sel.level.slice(1)} 🎯</div>}
      </div>
      <button onClick={()=>ok&&onDone({innings,level:sel.level,playerName:name.trim()||"Player"})} disabled={!ok} style={{width:"100%",padding:"14px",borderRadius:9,border:"none",cursor:ok?"pointer":"default",background:ok?"#231F20":"#ddd",color:ok?"#1FD71D":"#aaa",fontSize:15,fontWeight:700,fontFamily:"monospace",letterSpacing:"0.07em",textTransform:"uppercase"}}>
        Let's Play! ⚾
      </button>
    </div>
  );
}

// ── Coach Auth ─────────────────────────────────────────────────────────────

function CoachAuth({ onBack }) {
  const [tab,setTab]=useState("signin"),[email,setEmail]=useState(""),[password,setPassword]=useState("");
  const [name,setName]=useState(""),[teamName,setTeamName]=useState(""),[loading,setLoading]=useState(false),[error,setError]=useState("");
  const [resetSent,setResetSent]=useState(false);
  const handleSignIn=async()=>{if(!email||!password){setError("Please fill in all fields.");return;}setLoading(true);setError("");setResetSent(false);try{await signInWithEmailAndPassword(auth,email,password);}catch{setError("Invalid email or password.");}setLoading(false);};
  const handleSignUp=async()=>{if(!name||!teamName||!email||!password){setError("Please fill in all fields.");return;}if(password.length<6){setError("Password must be at least 6 characters.");return;}setLoading(true);setError("");
    try{const{user}=await createUserWithEmailAndPassword(auth,email,password);const pic=genInviteCode(),cic=genInviteCode(),tr=doc(collection(db,"teams"));
      await setDoc(tr,{name:teamName,coachId:user.uid,playerInviteCode:pic,coachInviteCode:cic,createdAt:new Date().toISOString()});
      await setDoc(doc(db,"users",user.uid),{email,playerName:name,role:"coach",teamId:tr.id,createdAt:new Date().toISOString()});
    }catch(e){setError(e.message.includes("email-already-in-use")?"An account with this email already exists.":e.message);}setLoading(false);};
  const handleReset=async()=>{
    if(!email){setError("Enter your email above first, then tap 'Forgot password?'.");return;}
    setLoading(true);setError("");setResetSent(false);
    try{await sendPasswordResetEmail(auth,email);setResetSent(true);}
    catch{setError("Couldn't send reset email. Check the address and try again.");}
    setLoading(false);
  };
  return (
    <div style={{padding:"24px"}}>
      <button onClick={onBack} style={{fontSize:12,color:"#999",background:"none",border:"none",cursor:"pointer",marginBottom:16,padding:0}}>← Back</button>
      <div style={{fontSize:18,fontWeight:700,color:"#231F20",marginBottom:20,fontFamily:"monospace"}}>🏆 Coach Access</div>
      <div style={{display:"flex",marginBottom:20,background:"#f0f0f0",borderRadius:8,padding:3}}>
        {["signin","signup"].map(t=><button key={t} onClick={()=>{setTab(t);setError("");setResetSent(false);}} style={{flex:1,padding:"8px",borderRadius:6,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,background:tab===t?"#231F20":"transparent",color:tab===t?"#1FD71D":"#666",transition:"all 0.15s"}}>{t==="signin"?"Sign In":"Create Account"}</button>)}
      </div>
      {tab==="signup"&&<><input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" style={inputStyle}/><input value={teamName} onChange={e=>setTeamName(e.target.value)} placeholder="Team name (e.g. Tigers 12U)" style={inputStyle}/></>}
      <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email address" type="email" style={inputStyle}/>
      <input value={password} onChange={e=>setPassword(e.target.value)} placeholder="Password" type="password" style={inputStyle}/>
      {tab==="signin"&&<button onClick={handleReset} disabled={loading} style={{background:"none",border:"none",color:"#231F20",fontSize:12,cursor:"pointer",padding:0,marginBottom:12,textDecoration:"underline"}}>Forgot password?</button>}
      {resetSent&&<div style={{color:"#2e7d32",fontSize:12,marginBottom:10}}>Password reset email sent — check your inbox.</div>}
      {error&&<div style={{color:"#e53935",fontSize:12,marginBottom:10}}>{error}</div>}
      <button onClick={tab==="signin"?handleSignIn:handleSignUp} disabled={loading} style={{width:"100%",padding:"13px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:8,cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"monospace"}}>
        {loading?"Loading...":tab==="signin"?"Sign In":"Create Account & Team"}
      </button>
    </div>
  );
}

// ── Player Auth ─────────────────────────────────────────────────────────────

function PlayerAuth({ onBack, prefillName }) {
  const [tab,setTab]=useState("signin"),[email,setEmail]=useState(""),[password,setPassword]=useState("");
  const [playerName,setPlayerName]=useState(prefillName||""),[inviteCode,setInviteCode]=useState(""),[role,setRole]=useState("player");
  const [loading,setLoading]=useState(false),[error,setError]=useState("");
  const [resetSent,setResetSent]=useState(false);
  const handleSignIn=async()=>{if(!email||!password){setError("Please fill in all fields.");return;}setLoading(true);setError("");setResetSent(false);try{await signInWithEmailAndPassword(auth,email,password);}catch{setError("Invalid email or password.");}setLoading(false);};
  const handleSignUp=async()=>{if(!playerName||!email||!password||!inviteCode){setError("Please fill in all fields.");return;}if(password.length<6){setError("Password must be at least 6 characters.");return;}setLoading(true);setError("");
    try{const code=inviteCode.toUpperCase().trim(),cf=role==="assistant_coach"?"coachInviteCode":"playerInviteCode";
      const snap=await getDocs(query(collection(db,"teams"),where(cf,"==",code)));
      if(snap.empty){setError(role==="assistant_coach"?"Invalid coach invite code.":"Invalid player invite code. Ask your coach.");setLoading(false);return;}
      const{user}=await createUserWithEmailAndPassword(auth,email,password);
      await setDoc(doc(db,"users",user.uid),{email,playerName,role,teamId:snap.docs[0].id,createdAt:new Date().toISOString()});
    }catch(e){setError(e.message.includes("email-already-in-use")?"An account with this email already exists.":e.message);}setLoading(false);};
  const handleReset=async()=>{
    if(!email){setError("Enter your email above first, then tap 'Forgot password?'.");return;}
    setLoading(true);setError("");setResetSent(false);
    try{await sendPasswordResetEmail(auth,email);setResetSent(true);}
    catch{setError("Couldn't send reset email. Check the address and try again.");}
    setLoading(false);
  };
  return (
    <div style={{padding:"24px"}}>
      <button onClick={onBack} style={{fontSize:12,color:"#999",background:"none",border:"none",cursor:"pointer",marginBottom:16,padding:0}}>← Back</button>
      <div style={{fontSize:18,fontWeight:700,color:"#231F20",marginBottom:20,fontFamily:"monospace"}}>⚾ Player Access</div>
      <div style={{display:"flex",marginBottom:20,background:"#f0f0f0",borderRadius:8,padding:3}}>
        {["signin","signup"].map(t=><button key={t} onClick={()=>{setTab(t);setError("");setResetSent(false);}} style={{flex:1,padding:"8px",borderRadius:6,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,background:tab===t?"#231F20":"transparent",color:tab===t?"#1FD71D":"#666",transition:"all 0.15s"}}>{t==="signin"?"Sign In":"Create Account"}</button>)}
      </div>
      {tab==="signup"&&(<>
        <div style={{fontSize:11,color:"#888",marginBottom:5}}>Player's name (shown to coaches)</div>
        <input value={playerName} onChange={e=>setPlayerName(e.target.value)} placeholder="Player's first and last name" style={inputStyle}/>
        <input value={inviteCode} onChange={e=>setInviteCode(e.target.value)} placeholder="Team invite code from your coach" style={{...inputStyle,textTransform:"uppercase",letterSpacing:"0.12em"}}/>
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          {["player","assistant_coach"].map(r=><button key={r} onClick={()=>setRole(r)} style={{flex:1,padding:"9px",borderRadius:7,border:role===r?"2px solid #231F20":"1px solid #ddd",background:role===r?"#e3fbe6":"white",cursor:"pointer",fontSize:11,fontWeight:600,color:"#231F20"}}>{r==="player"?"⚾ Player":"🏆 Asst. Coach"}</button>)}
        </div>
        <div style={{fontSize:11,color:"#aaa",marginBottom:12,lineHeight:1.5}}>By creating an account, a parent or guardian confirms they have approved this account for their player.</div>
      </>)}
      <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email address" type="email" style={inputStyle}/>
      <input value={password} onChange={e=>setPassword(e.target.value)} placeholder="Password (min 6 characters)" type="password" style={inputStyle}/>
      {tab==="signin"&&<button onClick={handleReset} disabled={loading} style={{background:"none",border:"none",color:"#231F20",fontSize:12,cursor:"pointer",padding:0,marginBottom:12,textDecoration:"underline"}}>Forgot password?</button>}
      {resetSent&&<div style={{color:"#2e7d32",fontSize:12,marginBottom:10}}>Password reset email sent — check your inbox.</div>}
      {error&&<div style={{color:"#e53935",fontSize:12,marginBottom:10}}>{error}</div>}
      <button onClick={tab==="signin"?handleSignIn:handleSignUp} disabled={loading} style={{width:"100%",padding:"13px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:8,cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"monospace"}}>
        {loading?"Loading...":tab==="signin"?"Sign In":"Create Account"}
      </button>
    </div>
  );
}

// ── Session Summary ────────────────────────────────────────────────────────

function SessionSummary({ stats, wrongAnswers=[], onViewProgress, onNewPosition, isGuest, onSignUp }) {
  const [showReplay,setShowReplay]=useState(false);
  const accuracy=stats.total>0?Math.round((stats.correct/stats.total)*100):0;
  const color=getAccuracyColor(accuracy);
  const posLabel=POSITIONS.find(p=>p.id===stats.position)?.label||stats.position;

  return (
    <div style={{padding:"28px 22px",textAlign:"center"}}>
      <div style={{fontSize:44,marginBottom:10}}>⚾</div>
      <div style={{fontSize:20,fontWeight:700,color:"#231F20",marginBottom:4}}>Session Complete!</div>
      <div style={{fontSize:13,color:"#888",marginBottom:22}}>{posLabel}</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:18}}>
        {[{label:"Questions",val:stats.total},{label:"Correct",val:stats.correct},{label:"Accuracy",val:`${accuracy}%`}].map(s=>(
          <div key={s.label} style={{background:"#f6f6f6",borderRadius:9,padding:"12px 8px"}}>
            <div style={{fontSize:10,color:"#bbb",textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>{s.label}</div>
            <div style={{fontSize:22,fontWeight:700,color:s.label==="Accuracy"?color:"#231F20",fontFamily:"monospace"}}>{s.val}</div>
          </div>
        ))}
      </div>
      <div style={{marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#888",marginBottom:6}}>
          <span>Session accuracy</span><span style={{color,fontWeight:700}}>{accuracy}%</span>
        </div>
        <div style={{height:10,background:"#e8e8e8",borderRadius:5,overflow:"hidden"}}>
          <div style={{height:"100%",width:`${accuracy}%`,background:color,borderRadius:5}}/>
        </div>
      </div>

      {wrongAnswers.length>0&&(
        <div style={{marginBottom:20,textAlign:"left"}}>
          <button onClick={()=>setShowReplay(!showReplay)} style={{width:"100%",padding:"11px",background:showReplay?"#231F20":"#f6f6f6",color:showReplay?"#1FD71D":"#333",border:`1px solid ${showReplay?"#231F20":"#ddd"}`,borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,marginBottom:showReplay?10:0}}>
            📋 Review {wrongAnswers.length} Missed Play{wrongAnswers.length!==1?"s":""}  {showReplay?"▲":"▼"}
          </button>
          {showReplay&&wrongAnswers.map((wa,i)=>{
            const ci=wa.scenario?.correctIndices||[];
            return (
              <div key={i} style={{background:"#fff8e1",border:"1px solid #ffe082",borderRadius:8,padding:"12px 14px",marginBottom:8}}>
                <div style={{fontSize:12,color:"#856404",fontWeight:600,marginBottom:6}}>{wa.scenario?.situation}</div>
                <div style={{fontSize:13,fontWeight:700,color:"#231F20",fontStyle:"italic",marginBottom:8}}>"{wa.scenario?.ballInPlay}"</div>
                {wa.scenario?.choices?.map((c,j)=>{
                  const isCorrect=ci.includes(j), isWrong=j===wa.chosen&&!isCorrect;
                  return <div key={j} style={{fontSize:12,padding:"6px 10px",borderRadius:6,marginBottom:4,background:isCorrect?"#e8f5e9":isWrong?"#ffebee":"#f9f9f9",color:isCorrect?"#2e7d32":isWrong?"#c62828":"#666",border:`1px solid ${isCorrect?"#4caf50":isWrong?"#e53935":"#eee"}`}}>
                    {c} {isCorrect?"✓":""}{isWrong?"✗ (your answer)":""}
                  </div>;
                })}
                <div style={{fontSize:11,color:"#556b2f",marginTop:8,lineHeight:1.5,fontStyle:"italic"}}>{wa.scenario?.explanation}</div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {isGuest?(
          <>
            <div style={{background:"#e3fbe6",border:"1px solid #8fe39c",borderRadius:9,padding:"12px 14px",marginBottom:4}}>
              <div style={{fontSize:13,fontWeight:700,color:"#231F20",marginBottom:3}}>📊 Want to track your progress?</div>
              <div style={{fontSize:12,color:"#555",lineHeight:1.5,marginBottom:10}}>Create a free player account to save your stats and see how you improve over time.</div>
              <button onClick={onSignUp} style={{width:"100%",padding:"11px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"monospace"}}>Create Free Account</button>
            </div>
            <button onClick={onNewPosition} style={{padding:"14px",background:"white",color:"#231F20",border:"2.5px solid #231F20",borderRadius:9,cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"monospace"}}>⚾ Pick New Position</button>
          </>
        ):(
          <>
            <button onClick={onViewProgress} style={{padding:"14px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:9,cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"monospace"}}>📊 View My Progress</button>
            <button onClick={onNewPosition} style={{padding:"14px",background:"white",color:"#231F20",border:"2.5px solid #231F20",borderRadius:9,cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"monospace"}}>⚾ Pick New Position</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Player Dashboard ───────────────────────────────────────────────────────

function PlayerDashboard({ userProfile, onBack }) {
  const [logs,setLogs]=useState([]),[loading,setLoading]=useState(true);

  useEffect(()=>{
    const load=async()=>{
      setLoading(true);
      const snap=await getDocs(query(collection(db,"playLogs"),where("userId","==",userProfile.id)));
      setLogs(snap.docs.map(d=>({id:d.id,...d.data()})));
      setLoading(false);
    };
    load();
  },[userProfile.id]);

  const byPosition={};
  logs.forEach(log=>{
    if(!byPosition[log.position])byPosition[log.position]={total:0,correct:0};
    byPosition[log.position].total++;
    if(log.correct)byPosition[log.position].correct++;
  });

  const posStats=Object.entries(byPosition).map(([pos,s])=>({
    pos, label:POSITIONS.find(p=>p.id===pos)?.label||pos,
    total:s.total, correct:s.correct, accuracy:Math.round((s.correct/s.total)*100),
    stars:calcStars(s.correct,s.total),
  })).sort((a,b)=>b.total-a.total);

  const byType={};
  logs.forEach(log=>{
    if(!log.situationType)return;
    if(!byType[log.situationType])byType[log.situationType]={total:0,correct:0};
    byType[log.situationType].total++;
    if(log.correct)byType[log.situationType].correct++;
  });
  const MIN_TYPE_SAMPLE=3;
  const typeStats=Object.entries(byType).map(([type,s])=>({
    type, label:SITUATION_TYPES.find(t=>t.id===type)?.label||type,
    total:s.total, correct:s.correct, accuracy:Math.round((s.correct/s.total)*100),
  })).filter(s=>s.total>=MIN_TYPE_SAMPLE).sort((a,b)=>a.accuracy-b.accuracy);
  const weakestTypes=typeStats.filter(s=>s.accuracy<70).slice(0,3);

  const totalPlayed=logs.length, totalCorrect=logs.filter(l=>l.correct).length;
  const overallAccuracy=totalPlayed>0?Math.round((totalCorrect/totalPlayed)*100):0;
  const myLevel=calcLevel(totalCorrect);
  const dailyStreak=userProfile?.dailyStreak||0;
  const maxStreak=userProfile?.maxStreak||0;
  const earnedBadges=checkBadges(logs,maxStreak,dailyStreak);

  return (
    <div style={{padding:"18px 18px 28px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div style={{fontSize:16,fontWeight:700,color:"#231F20"}}>📊 My Progress</div>
        <button onClick={onBack} style={{fontSize:11,color:"#888",background:"none",border:"1px solid #ddd",borderRadius:5,padding:"4px 10px",cursor:"pointer"}}>← Back</button>
      </div>

      {loading&&<div style={{textAlign:"center",padding:"40px 0",color:"#888"}}>Loading your stats...</div>}

      {!loading&&(<>
        {/* Overall card with level */}
        <div style={{background:"#231F20",borderRadius:10,padding:"16px 18px",marginBottom:16}}>
          <div style={{fontSize:10,color:"#5fcf70",marginBottom:10,letterSpacing:"0.06em",textTransform:"uppercase"}}>Overall Stats</div>
          <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:12}}>
            <div style={{textAlign:"center",background:"rgba(245,200,66,0.15)",borderRadius:10,padding:"10px 18px",border:"1.5px solid rgba(245,200,66,0.4)",flexShrink:0}}>
              <div style={{fontSize:9,color:"#5fcf70",textTransform:"uppercase",marginBottom:2}}>Level</div>
              <div style={{fontSize:40,fontWeight:900,fontFamily:"monospace",color:"#1FD71D",lineHeight:1}}>{myLevel}</div>
              <div style={{fontSize:9,color:"#8fe39c",marginTop:2}}>{totalCorrect%10}/10 to next</div>
            </div>
            <div style={{flex:1,display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {[{label:"Plays",val:totalPlayed},{label:"Correct",val:totalCorrect},{label:"Accuracy",val:totalPlayed>0?`${overallAccuracy}%`:"—"},{label:"Streak 🔥",val:`${dailyStreak} days`}].map(s=>(
                <div key={s.label} style={{textAlign:"center"}}>
                  <div style={{fontSize:8,color:"#5fcf70",textTransform:"uppercase",marginBottom:2}}>{s.label}</div>
                  <div style={{fontSize:15,fontWeight:700,fontFamily:"monospace",color:"#1FD71D"}}>{s.val}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{height:6,background:"rgba(255,255,255,0.15)",borderRadius:3,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${(totalCorrect%10)/10*100}%`,background:"#1FD71D",borderRadius:3}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:"#5fcf70",marginTop:4}}>
            <span>Level {myLevel}</span><span>Level {myLevel+1}</span>
          </div>
        </div>

        {/* Badges */}
        {earnedBadges.length>0&&(
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:"#888",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.06em"}}>🏅 Badges Earned</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
              {earnedBadges.map(bid=>{
                const badge=BADGE_DEFINITIONS.find(b=>b.id===bid);
                if(!badge)return null;
                return (
                  <div key={bid} style={{background:"#231F20",borderRadius:8,padding:"8px 4px",textAlign:"center"}}>
                    <div style={{fontSize:22,marginBottom:3}}>{badge.icon}</div>
                    <div style={{fontSize:9,fontWeight:700,color:"#1FD71D",lineHeight:1.3}}>{badge.name}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Areas to work on */}
        {weakestTypes.length>0&&(
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:"#888",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.06em"}}>🎯 Areas to Work On</div>
            {weakestTypes.map(s=>{
              const color=getAccuracyColor(s.accuracy);
              return (
                <div key={s.type} style={{background:"#fff8e1",border:"1px solid #ffe082",borderRadius:9,padding:"10px 14px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:"#856404"}}>{s.label}</div>
                    <div style={{fontSize:10,color:"#a17f00"}}>{s.correct}/{s.total} correct</div>
                  </div>
                  <div style={{fontSize:18,fontWeight:700,fontFamily:"monospace",color}}>{s.accuracy}%</div>
                </div>
              );
            })}
          </div>
        )}

        {posStats.length===0&&<div style={{textAlign:"center",padding:"30px",color:"#bbb",fontSize:13}}>No plays yet! Start training to see your progress here.</div>}

        {posStats.length>0&&(
          <>
            <div style={{fontSize:11,fontWeight:700,color:"#888",marginBottom:12,textTransform:"uppercase",letterSpacing:"0.06em"}}>By Position</div>
            {posStats.map(s=>{
              const color=getAccuracyColor(s.accuracy);
              const grade=s.accuracy>=85?"Strong 💪":s.accuracy>=70?"Good 👍":s.accuracy>=50?"Keep Going 📚":"Needs Work 🔄";
              return (
                <div key={s.pos} style={{background:"#f8f8f8",borderRadius:10,padding:"14px 16px",marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{background:"#231F20",color:"#1FD71D",fontSize:13,fontWeight:700,padding:"5px 11px",borderRadius:6,fontFamily:"monospace"}}>{s.pos}</div>
                      <div>
                        <div style={{fontSize:13,fontWeight:600,color:"#231F20"}}>{s.label}</div>
                        <div style={{fontSize:10,color:"#aaa"}}>
                          {s.total} questions · {s.correct} correct
                          <span style={{marginLeft:6}}>{"★".repeat(s.stars)}{"☆".repeat(5-s.stars)}</span>
                        </div>
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:22,fontWeight:700,fontFamily:"monospace",color}}>{s.accuracy}%</div>
                      <div style={{fontSize:10,color:"#aaa"}}>{grade}</div>
                    </div>
                  </div>
                  <div style={{height:8,background:"#e0e0e0",borderRadius:4,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${s.accuracy}%`,background:color,borderRadius:4}}/>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </>)}
    </div>
  );
}

// ── Onboarding ─────────────────────────────────────────────────────────────

function Onboarding({ playerName, onDone }) {
  const [innings,setInnings]=useState(null);
  const sel=INNINGS_OPTS.find(o=>o.innings===innings), ok=innings!==null;
  return (
    <div style={{padding:"28px 22px"}}>
      <div style={{textAlign:"center",marginBottom:28}}>
        <div style={{fontSize:42,marginBottom:8}}>⚾</div>
        <div style={{fontSize:18,fontWeight:700,color:"#231F20"}}>Hey {playerName}! 👋</div>
        <div style={{fontSize:13,color:"#999",marginTop:4}}>Let's set up your training</div>
      </div>
      <div style={{marginBottom:26}}>
        <label style={{fontSize:13,fontWeight:600,color:"#444",display:"block",marginBottom:10,textAlign:"center"}}>How many innings do you play in a game?</label>
        <div style={{display:"flex",justifyContent:"center",gap:16,maxWidth:280,margin:"0 auto"}}>
          {INNINGS_OPTS.map(o=>{
            const active=innings===o.innings;
            return <button key={o.innings} onClick={()=>setInnings(o.innings)} style={{flex:1,padding:"20px 10px",borderRadius:9,cursor:"pointer",textAlign:"center",border:active?"2.5px solid #231F20":"1.5px solid #e0e0e0",background:active?"#231F20":"white",color:active?"#1FD71D":"#333",transition:"all 0.15s"}}>
              <div style={{fontSize:32,fontWeight:700,fontFamily:"monospace"}}>{o.innings}</div>
              <div style={{fontSize:11,marginTop:4,color:active?"#8fe39c":"#888"}}>{o.tag}</div>
            </button>;
          })}
        </div>
        {sel&&<div style={{textAlign:"center",fontSize:12,color:"#231F20",fontWeight:700,marginTop:10}}>Level: {sel.level.charAt(0).toUpperCase()+sel.level.slice(1)} 🎯</div>}
      </div>
      <button onClick={()=>ok&&onDone({innings,level:sel.level})} disabled={!ok} style={{width:"100%",padding:"14px",borderRadius:9,border:"none",cursor:ok?"pointer":"default",background:ok?"#231F20":"#ddd",color:ok?"#1FD71D":"#aaa",fontSize:15,fontWeight:700,fontFamily:"monospace",letterSpacing:"0.07em",textTransform:"uppercase",transition:"all 0.2s"}}>
        Let's Play! ⚾
      </button>
    </div>
  );
}

// ── Position Tip ───────────────────────────────────────────────────────────

function PositionTip({ position, onDismiss }) {
  const tips = POSITION_TIPS[position] || [];
  const [tip] = useState(() => tips[Math.floor(Math.random()*tips.length)] || "Know your role before every pitch!");
  const posInfo = POSITIONS.find(p=>p.id===position);
  return (
    <div style={{padding:"28px 22px"}}>
      <div style={{textAlign:"center",marginBottom:20}}>
        <div style={{display:"inline-flex",alignItems:"center",gap:10,background:"#231F20",color:"#1FD71D",padding:"6px 18px",borderRadius:999,fontSize:14,fontWeight:700,fontFamily:"monospace"}}>
          {position} — {posInfo?.label}
        </div>
      </div>
      <div style={{background:"#231F20",borderRadius:10,padding:"20px",marginBottom:24}}>
        <div style={{fontSize:11,color:"#5fcf70",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>💡 Coach's Tip Before You Play</div>
        <div style={{fontSize:16,color:"white",lineHeight:1.65,fontStyle:"italic"}}>"{tip}"</div>
      </div>
      <button onClick={onDismiss} style={{width:"100%",padding:"15px",background:"#1FD71D",color:"#231F20",border:"none",borderRadius:9,cursor:"pointer",fontSize:15,fontWeight:700,fontFamily:"monospace",letterSpacing:"0.05em"}}>
        Got it! Let's Play ⚾
      </button>
    </div>
  );
}

// ── App Shell ──────────────────────────────────────────────────────────────

export default function App() {
  const [screen,setScreen]=useState("loading"),[userProfile,setUserProfile]=useState(null);
  const [gameScreen,setGameScreen]=useState("onboarding"),[gameInfo,setGameInfo]=useState(null),[position,setPosition]=useState(null);
  const [sessionStats,setSessionStats]=useState(null),[isGuest,setIsGuest]=useState(false),[guestInfo,setGuestInfo]=useState(null);
  const [scenarioQueue,setScenarioQueue]=useState(null),[prefillPlayerName,setPrefillPlayerName]=useState("");

  const loadProfile=useCallback(async(uid)=>{
    try{
      const snap=await getDoc(doc(db,"users",uid));
      if(snap.exists()){const p={id:uid,...snap.data()};setUserProfile(p);setScreen(p.role==="player"?"player-game":"coach-dashboard");}
      else{setTimeout(async()=>{const snap2=await getDoc(doc(db,"users",uid));if(snap2.exists()){const p={id:uid,...snap2.data()};setUserProfile(p);setScreen(p.role==="player"?"player-game":"coach-dashboard");}else{await signOut(auth);setScreen("landing");}},1500);}
    }catch{setScreen("landing");}
  },[]);

  useEffect(()=>{
    const unsub=onAuthStateChanged(auth,user=>{
      if(user){loadProfile(user.uid);}else{setUserProfile(null);setScreen("landing");}
    });
    return unsub;
  },[loadProfile]);

  const handleSignOut=async()=>{await signOut(auth);setGameScreen("onboarding");setGameInfo(null);setPosition(null);};

  const handleGuestStart=(info)=>{
    setIsGuest(true);setGuestInfo(info);setGameInfo({innings:info.innings,level:info.level});
    setGameScreen("select");setScreen("player-game");
  };

  const handleGuestExit=()=>{setIsGuest(false);setGuestInfo(null);setGameInfo(null);setPosition(null);setSessionStats(null);setGameScreen("onboarding");setScreen("landing");};

  const handlePlayScenarios=(scenarios)=>{setScenarioQueue(scenarios);setPosition(null);setGameScreen("play");};

  const logPlay=async(pos,correct,scenarioData)=>{
    if(isGuest||!userProfile?.id||!userProfile?.teamId)return;
    try{
      await addDoc(collection(db,"playLogs"),{
        userId:userProfile.id,teamId:userProfile.teamId,position:pos,correct,
        situationType:scenarioData?.situationType||null,
        inning:scenarioData?.inning, teamScore:scenarioData?.teamScore, opponentScore:scenarioData?.opponentScore,
        createdAt:new Date().toISOString(),
      });
      const today=new Date().toISOString().substring(0,10);
      if(userProfile.lastPlayDate!==today){
        const yesterday=new Date(Date.now()-86400000).toISOString().substring(0,10);
        const newStreak=userProfile.lastPlayDate===yesterday?(userProfile.dailyStreak||0)+1:1;
        await updateDoc(doc(db,"users",userProfile.id),{lastPlayDate:today,dailyStreak:newStreak});
        setUserProfile(prev=>({...prev,lastPlayDate:today,dailyStreak:newStreak}));
      }
    }catch{}
  };

  const handleEndSession=async(stats)=>{
    setSessionStats(stats);
    setGameScreen("session-summary");
    if(!isGuest&&userProfile?.id&&stats.maxSessionStreak>(userProfile.maxStreak||0)){
      try{
        await updateDoc(doc(db,"users",userProfile.id),{maxStreak:stats.maxSessionStreak});
        setUserProfile(prev=>({...prev,maxStreak:stats.maxSessionStreak}));
      }catch{}
    }
  };

  const playerName=isGuest?(guestInfo?.playerName||"Player"):userProfile?.playerName;

  return (
    <div className="cp-app" style={{fontFamily:"'Georgia',serif"}}>
      <div className="cp-container" style={{margin:"0 auto",boxShadow:"0 6px 24px rgba(0,0,0,0.1)",borderRadius:12}}>
        <div style={{background:"#231F20",padding:"14px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",borderRadius:"12px 12px 0 0"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:22}}>⚾</span>
            <div>
              <div style={{fontSize:15,fontWeight:700,color:"#e3fbe6",fontFamily:"monospace",letterSpacing:"0.05em",textTransform:"uppercase"}}>Clutch Position</div>
              <div style={{fontSize:10,color:"#5fcf70"}}>Defensive Situations Trainer</div>
            </div>
          </div>
          {(userProfile||isGuest)&&<div style={{fontSize:12,color:"#8fe39c",fontFamily:"monospace"}}>{isGuest?`${playerName} ⚡`:userProfile?.playerName}</div>}
        </div>

        <div style={{background:"white",border:"1px solid #e0e0e0",borderTop:"none",borderRadius:"0 0 12px 12px",minHeight:480}}>
          {screen==="loading"&&<div style={{textAlign:"center",padding:"70px 0"}}><div style={{fontSize:30,display:"inline-block",animation:"spin 1.8s linear infinite"}}>⚾</div></div>}
          {screen==="landing"&&<Landing onCoach={()=>setScreen("coach-auth")} onPlayer={()=>setScreen("player-auth")} onGuest={()=>setScreen("guest-setup")}/>}
          {screen==="guest-setup"&&<GuestSetup onBack={()=>setScreen("landing")} onDone={handleGuestStart}/>}
          {screen==="coach-auth"&&<CoachAuth onBack={()=>setScreen("landing")}/>}
          {screen==="player-auth"&&<PlayerAuth onBack={()=>setScreen("landing")} prefillName={prefillPlayerName}/>}
          {screen==="coach-dashboard"&&userProfile&&<CoachDashboard userProfile={userProfile} onSignOut={handleSignOut}/>}
          {screen==="player-game"&&(userProfile||isGuest)&&(<>
            {gameScreen==="onboarding"&&<Onboarding playerName={playerName} onDone={info=>{setGameInfo(info);setGameScreen("select");}}/>}
            {gameScreen==="select"&&<PositionSelect info={{...gameInfo,name:playerName}} onSelect={p=>{setScenarioQueue(null);setPosition(p.id);setGameScreen("tip");}} onPlayScenarios={handlePlayScenarios} onViewPlays={!isGuest?()=>setGameScreen("plays"):null} onSignOut={isGuest?handleGuestExit:handleSignOut} onViewDashboard={!isGuest?()=>setGameScreen("dashboard"):null} isGuest={isGuest} userProfile={userProfile}/>}
            {gameScreen==="tip"&&<PositionTip position={position} onDismiss={()=>setGameScreen("play")}/>}
            {gameScreen==="play"&&<PlayScreen info={{...gameInfo,name:playerName}} position={position} scenarioQueue={scenarioQueue} userId={isGuest?null:userProfile?.id} onBack={()=>{setScenarioQueue(null);setGameScreen("select");}} onLogPlay={logPlay} onEndSession={handleEndSession}/>}
            {gameScreen==="session-summary"&&sessionStats&&<SessionSummary stats={sessionStats} wrongAnswers={sessionStats.wrongAnswers||[]} isGuest={isGuest} onViewProgress={()=>setGameScreen("dashboard")} onNewPosition={()=>{setScenarioQueue(null);setGameScreen("select");}} onSignUp={()=>{setPrefillPlayerName(guestInfo?.playerName&&guestInfo.playerName!=="Player"?guestInfo.playerName:"");setIsGuest(false);setGuestInfo(null);setScreen("player-auth");setGameScreen("onboarding");}}/>}
            {gameScreen==="dashboard"&&!isGuest&&<PlayerDashboard userProfile={userProfile} onBack={()=>setGameScreen("select")}/>}
            {gameScreen==="plays"&&!isGuest&&<TeamPlays userProfile={userProfile} onBack={()=>setGameScreen("select")}/>}
          </>)}
        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; }
        button { touch-action: manipulation; }
        input, select, textarea { font-size: 16px !important; }
        .cp-app { padding: 24px 16px; background: #f4f6f4; min-height: 100vh; }
        .cp-container { width: 100%; max-width: 640px; }
        @media (max-width: 768px) { .cp-app { padding: 16px 12px; } .cp-container { max-width: 100%; } }
        @media (max-width: 520px) { .cp-app { padding: 0; background: white; min-height: 100vh; } .cp-container { border-radius: 0 !important; box-shadow: none !important; } .cp-container > div:first-child { border-radius: 0 !important; } .cp-container > div:last-child { border-radius: 0 !important; min-height: 100vh; } button { min-height: 40px; } }
        @keyframes floatUp { 0%{opacity:1;transform:translateY(0)} 70%{opacity:1;transform:translateY(-18px)} 100%{opacity:0;transform:translateY(-28px)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}
